import React, {Component} from 'react';
import equals from 'fast-deep-equal';
import shortid from 'shortid';
import segment from "segment";

import Config from 'config.js';

import {Context} from "context/index"
import * as mapActions from "context/mapActions";
import {createHexRefresher} from "context/hexRefresh";
import {createMapWrites, hasMapWrite} from "context/mapWrites";
import {openProject} from "context/openFlow";
import buildInfo from "util/buildInfo";
import {startVersionCheck} from "util/versionCheck";
import {ping} from "repository/Api";
import * as ProjectRepository from "repository/ProjectRepository";
import {ApiError, isMapNotFound} from "repository/errors";
import {UnreadableMapError} from "util/mapModel";
import {reportError} from "util/reportError";
import {isDailyCap, isReadLimited, mapReadLimiter} from "util/rateLimit";
import * as Windows from 'util/Windows';

import hints from 'text/hints';
import groupNames from 'text/groupNames';
import shortcuts from 'text/shortcuts';
import copy from 'text/copy'
import {COPY} from "text/errors";
import * as UserController from "controller/UserController";
import * as ProjectMapController from "controller/MapController";
import * as UIController from "controller/UIController";
import * as TicketController from "controller/TicketController";

const url = new URL(window.location);
const projectId = url.pathname.split("/")[1];
const mapPack = url.searchParams.get("mapPack");

// A toast that asks a question and has not been answered: a confirm whose callback is still to be called.
const isPendingConfirm = toast => toast.type === "confirm" && Boolean(toast.callBack);

// The most windows a layout holds, open ones and the kept ones together.
const MAX_WINDOWS = 9;

// What the context keeps of a calibration read: the file as uploaded, and the working file, which is the uploaded one for a project that has none (the hex
// viewer's own fallback, HexViewer.js).
const calibrationState = ({data, dataModified}) => ({calibrationData: data, modifiedCalibrationData: dataModified ?? data});

export default class ContextProvider extends Component {
    state = {
        //user
        user: {},
        lightTheme: false,
        setLightTheme: this.setLightTheme.bind(this),

        //project
        id: "",
        mapPack: "",
        mapPacks: [],
        metadata: {},
        folderStructure: [],
        calibrationData: "",
        modifiedCalibrationData: "",
        loadedMaps: {},
        setMapModified: this.setMapModified.bind(this),
        setMapModifiedAxis: this.setMapModifiedAxis.bind(this),
        toggleMapFavourited: this.toggleMapFavourited.bind(this),
        undo: this.undo.bind(this),
        resetMap: this.resetMap.bind(this),

        //open flow
        screen: null,
        retryOpen: this.retryOpen.bind(this),

        //ui
        loaded: false,
        updateAvailable: false,
        text: {copy, hints, groupNames, shortcuts},
        windows: [],
        toasts: [],
        touch: false,
        networkConnected: true,
        blackout: false,
        disclaimerConfirmed: false,
        fullScreen: false,
        updateWindowPos: this.updateWindowPos.bind(this),
        activateAxisEditing: this.activateAxisEditing.bind(this),
        deactivateAxisEditing: this.deactivateAxisEditing.bind(this),
        openWindow: this.openWindow.bind(this),
        closeWindow: this.closeWindow.bind(this),
        setSelectedWindow: this.setSelectedWindow.bind(this),
        getSelectedWindow: this.getSelectedWindow.bind(this),
        confirmDisclaimer: this.confirmDisclaimer.bind(this),
        toggleFullScreen: this.toggleFullScreen.bind(this),
        setFullScreen: this.setFullScreen.bind(this),
        alert: this.alert.bind(this),
        confirm: this.confirm.bind(this),
        unsetToast: this.unsetToast.bind(this),
        reportMapIssue: this.reportMapIssue.bind(this),
        setHexViewerVisible: this.setHexViewerVisible.bind(this)
    };

    // Whether the hex viewer is shown (the Editor starts with it shown), and the one refresher that pulls the calibration after the writes that changed bytes,
    // while it is (context/hexRefresh.js).
    hexVisible = true;
    refresher = createHexRefresher({
        fetchCalibration: () => ProjectRepository.getCalibration(projectId),
        onData: calibration => this.setState(calibrationState(calibration)),
        isVisible: () => this.hexVisible,
    });

    // The queue of each map's writes (context/mapWrites.js): one write of a map is on the wire at a time, and the actions find the queue in their ctx.
    mapWrites = createMapWrites();

    // The stored windows that are in the layout and not open: a window whose load the API's read limit refused (a 429; Task 7 for a burst, Task 14 for the daily
    // cap). It is not opened (no read is made for it until the next project open), and every write of the layout carries it, so that the first move of another
    // window does not drop it. It leaves the list when the user opens it. Entries are the stored ones, in their stored order.
    keptWindows = [];

    // Stops the version check (util/versionCheck.js), which starts after the project load.
    stopVersionCheck = null;
    unmounted = false;

    componentWillUnmount() {
        this.unmounted = true;
        if(this.stopVersionCheck) this.stopVersionCheck();
        this.refresher.dispose();
        mapReadLimiter.setHandlers({});
    }

    async componentDidMount() {
        // The limiter tells the user: a burst with a toast that says how long the pause is, the daily cap with a modal (Ok only; the open windows stay usable).
        mapReadLimiter.setHandlers({
            onBurst: seconds => this.alert(COPY.map_read_burst(seconds), "neutral"),
            onDailyCap: () => this.confirm(COPY.map_read_daily_cap, "danger", false),
        });
        try {
            const user = await UserController.getUser();
            this.setState({user});
            await this._open();
        } catch (e) {
            this._failOpen(e);
        }
    }

    /**
     * Runs the open flow again from its first step, for the Retry button of the screens that offer it.
     */
    async retryOpen() {
        try {
            this.setState({screen: null});
            await this._open();
        } catch (e) {
            this._failOpen(e);
        }
    }

    /**
     * Opens the project (context/openFlow.js). When the flow stops on a screen, or on the redirect to the dashboard, the screen stays and the editor
     * does not load. Otherwise the screen is cleared, the windows that were open are loaded and the editor mounts.
     */
    async _open() {
        const result = await openProject({
            projectId,
            mapPack,
            show: screen => this.setState({screen}),
            notify: copyKey => this.alert(COPY[copyKey], "neutral"),
        });
        if (!result.project) return;

        this.setState({screen: null});
        const {id, ...metadata} = result.project;
        const {mapPack: chosenMapPack, mapPacks, folderStructure, calibration} = result;
        const UI = UIController.getUI(projectId);

        //make sure that only maps in current folder structure are opened - deals with differences between map packs, and with the windows stored
        //under the API's old UUID ids, which name no map now that maps are identified by name (R55)
        const storedWindows = UI.windows;
        const listedWindows = Windows.filterStoredWindows(storedWindows, folderStructure);

        const {loaded, failed, kept = []} = await ProjectMapController.getMaps(projectId, listedWindows.map(window => window.mapId));
        const {dropped, refused} = this._handleFailedMaps(failed);

        //The layout is written back once, after the maps are read, when a window was dropped for either reason, so the drop happens once and not at
        //every load. Both only remove windows, so a different length means a different layout. A window that the read limit refused (a 429: a burst that
        //outlasted the retry, or the daily cap) stays in the stored layout and is not opened; it is kept (keptWindows) through the later writes of the layout, and
        //opens with the next project open.
        const layout = listedWindows.filter(window => !dropped.has(window.mapId));
        if(layout.length !== storedWindows.length) UIController.setWindows(layout, projectId);
        const notOpened = new Set([...refused, ...kept]);
        UI.windows = layout.filter(window => !notOpened.has(window.mapId));
        this.keptWindows = layout.filter(window => notOpened.has(window.mapId));

        // Like every other write of loadedMaps, the load merges its maps into the loadedMaps of the moment.
        this.setState(state => ({
            id,
            mapPack: chosenMapPack,
            mapPacks,
            metadata,
            folderStructure,
            ...calibrationState(calibration),
            ...UI,
            loaded: true,
            loadedMaps: {...state.loadedMaps, ...loaded},
        }));
        // A project without a name (the recorded v2 project has none) keeps the page's own title, that of public/index.html.
        document.title = typeof metadata.projectName === 'string' && metadata.projectName !== '' ? `${metadata.projectName} - Speedweaver` : 'Speedweaver';

        // The server may serve a newer build while this one is open: check every ten minutes and on focus (R56), once, and show the reload banner.
        if(!this.stopVersionCheck && !this.unmounted) this.stopVersionCheck = startVersionCheck({current: buildInfo, onChange: () => this.setState({updateAvailable: true})});

        await segment.track("project_opened", {
            application: "editor",
            projectId: this.state.id,
            projectType: this.state.metadata.projectType === "DEMO" ? "demo" : "user",
        })

        await this._checkNetworkConnection();
        window.addEventListener('online', this._checkNetworkConnection.bind(this));
        window.addEventListener('offline', this._checkNetworkConnection.bind(this));
    }

    /**
     * Tells the user about the stored windows whose map did not load, and sorts those windows. The window of a map the release lacks goes without a
     * word (R45), an unreadable map says so, and the read limit (a 429) is left to the limiter, which has told the user: its window waits, unopened, in the
     * layout. Anything else is reported to
     * Sentry, with the project, the map and what the error says, and shown as COPY.generic, since a window that vanishes without a word is worse than
     * one that says why.
     * @param {{name: string, error: Error}[]} failed - from getMaps
     * @returns {{dropped: Set<string>, refused: Set<string>}} the windows to drop from the layout, and the windows the read limit refused
     */
    _handleFailedMaps(failed) {
        const dropped = new Set();
        const refused = new Set();

        failed.forEach(({name, error}) => {
            if (isReadLimited(error)) {
                refused.add(name);
                return;
            }

            dropped.add(name);
            if (isMapNotFound(error)) return;
            if (error instanceof UnreadableMapError) {
                this.alert(COPY.mapUnreadable, "danger");
                return;
            }

            console.error(error);
            reportError(error, {projectId, mapName: name});
            this.alert(COPY.generic, "danger");
        });

        return {dropped, refused};
    }

    /**
     * Anything the open flow did not map: reported, and the page goes back to the dashboard.
     */
    _failOpen(e) {
        console.error(e);
        console.log(e)
        reportError(e, {projectId});
        window.location = Config.dashboard
    }

    setLightTheme(theme){
        this.setState({
            lightTheme: theme
        })
    }

    /**
     * The Editor tells the context when the hex viewer is shown or hidden. The calibration is pulled only while it is shown, and at once when it is
     * shown again after a write that changed bytes.
     * @param {boolean} visible
     */
    setHexViewerVisible(visible) {
        this.hexVisible = visible;
        this.refresher.noteVisibility(visible);
    }

    //project public methods

    /**
     * What the map actions (context/mapActions.js) run against: this provider's state, toasts and window layout, and Sentry with the project bound.
     * The one shape serves every action, including one that reads other maps than its own: the action names the map in a report, so that the report
     * says which map it is for. `setState` is the provider's, so it takes an object or a function of the state. `mapWrites` is the provider's one
     * queue of each map's writes, the same for every action. `retryOptions` is left out, so that the retries and the hold after a lost request wait on
     * real timers. `hexDirty` tells the refresher of the hex viewer that a write changed bytes: the number of bytes changed (0 does nothing), or `true` for bytes that are not counted.
     */
    ctx() {
        return {
            projectId,
            getState: () => ({loadedMaps: this.state.loadedMaps, folderStructure: this.state.folderStructure, windows: this.state.windows}),
            setState: changes => this.setState(changes),
            alert: (message, bgColour) => this.alert(message, bgColour),
            confirm: (message, bgColour, cancelable) => this.confirm(message, bgColour, cancelable),
            closeWindow: mapId => this.closeWindow(mapId),
            hexDirty: changed => this.refresher.noteWrite(changed),
            captureException: (error, mapName) => reportError(error, {projectId, mapName}),
            mapWrites: this.mapWrites,
        };
    }

    /**
     * Saves the values of a map's data. The action says what went wrong to the user itself, so this settles when the save has, whatever came of it,
     * and a caller that awaits it (Window.save) is held until then, a Retry that the user has not answered included.
     * @param {string} mapId
     * @param {number[][]} values - the matrix values of the map's data axis
     */
    setMapModified(mapId, values) {
        return mapActions.saveMap(this.ctx(), mapId, values);
    }

    /**
     * Saves the values of one of a map's axes. The action says what went wrong to the user itself, so this settles when the save has (the re-reads of
     * the other windows it affected are not waited for), and a caller that awaits it (Window.save) is held until then, a Retry that the user has not
     * answered included.
     * @param {string} mapId
     * @param {'X'|'Y'} axis - the letter of the axis (axisLetter)
     * @param {number[]} values - the flat list of the axis's values
     */
    setMapModifiedAxis(mapId, axis, values) {
        return mapActions.saveAxis(this.ctx(), mapId, axis, values);
    }

    /**
     * Stars or unstars a map. Not a step of the map's queue (mapActions.toggleFavourite): the star follows the API's answer, and nothing is read. The action
     * says what went wrong to the user itself; this settles when it has.
     * @param {string} mapId
     */
    toggleMapFavourited(mapId) {
        return mapActions.toggleFavourite(this.ctx(), mapId);
    }

    /**
     * Reverts the newest entry of a map's undo list. A step of the map's queue (mapActions.undo), like a save, so that it runs after the writes asked for
     * before it and no write of the map is made outside the queue. The action says what went wrong to the user itself; this settles when it has.
     * @param {string} mapId
     */
    undo(mapId) {
        return mapActions.undo(this.ctx(), mapId);
    }

    /**
     * Puts a map back to its uploaded values. A step of the map's queue (mapActions.resetMap), like a save: it runs after the writes asked for before it.
     * The action says what went wrong to the user itself; this settles when it has.
     * @param {string} mapId
     * @param {?string} axisEditingAxisName - 'xAxis' or 'yAxis' for a window that is editing an axis: only that axis is reset
     */
    resetMap(mapId, axisEditingAxisName) {
        return mapActions.resetMap(this.ctx(), mapId, axisEditingAxisName);
    }

    /**
     * Stores the layout: the open windows, and the kept ones (keptWindows) that are not open, which come first, in their stored order, so that the open windows
     * keep the front. Every write of the layout goes through here, so that none drops a window the read limit kept. The open windows come first in the
     * count: the layout holds at most nine windows in all, so when the kept ones do not fit the earliest stored are left out of this write (they stay in
     * keptWindows, and come back into a later write when there is room).
     * @param {{mapId: string}[]} windows - the open windows (state.windows)
     */
    _storeWindows(windows) {
        const open = new Set(windows.map(window => window.mapId));
        const notOpen = this.keptWindows.filter(window => !open.has(window.mapId));
        const room = Math.max(0, MAX_WINDOWS - windows.length);

        UIController.setWindows([...notOpen.slice(Math.max(0, notOpen.length - room)), ...windows], projectId);
    }

    //UI public methods

    updateWindowPos(id, x, y) {
        const windows = Windows.updateWindowPos(id, x, y, this.state.windows);

        if(!equals(windows, this.state.windows)){
            this._storeWindows(windows);
            this.setState({windows});
        }
    }

    activateAxisEditing(mapId, axisName) {
        if (axisName !== "xAxis" && axisName !== "yAxis") throw new Error(`Invalid Axis Name: ${axisName}`)
        const windows = Windows.activateAxisEditing(mapId, axisName, this.state.windows);

        this._storeWindows(windows);
        this.setState({windows});
    }

    deactivateAxisEditing(mapId) {
        const windows = Windows.deactivateAxisEditing(mapId, this.state.windows);

        this._storeWindows(windows);
        this.setState({windows});
    }

    async openWindow(mapId) {
        // The kept entry of the map, and the kept list as it was, for a click that the read limit refuses (below).
        const keptBefore = this.keptWindows;
        const kept = keptBefore.find(window => window.mapId === mapId);

        try {
            const alreadyOpen = this.state.windows.some(window => window.mapId === mapId);

            if(!alreadyOpen && (this.state.windows.length < MAX_WINDOWS)){
                const windows = Windows.openWindow(mapId, this.state.windows);

                // A window the read limit kept is open now, and no longer kept: it is stored once, in its new place.
                this.keptWindows = this.keptWindows.filter(window => window.mapId !== mapId);
                this._storeWindows(windows);
                this.setState({windows});

                // A map that is loaded and has a write in flight is not read again. The window shows the loaded map, and the write's answer refreshes
                // it: a read sent now could answer after the write has landed, and put the older map back on screen (the Fable ruling, R6). Any other
                // read of a loaded map is a step of that map's queue too (refreshMap), so a save made while the read is out waits for it, and the read
                // never lands after the save. The step puts the map on screen itself, and deals with its own failure: a map that cannot be read is
                // left on its last payload, and a map the release lacks closes the window quietly.
                if (this.state.loadedMaps[mapId]) {
                    if (!hasMapWrite(this.ctx(), mapId)) await mapActions.refreshMap(this.ctx(), mapId);
                    return;
                }

                // The map is merged into the loadedMaps of the moment it arrives. Reading this.state.loadedMaps before the await would write it into the
                // object that was there when the open began, and a save that landed meanwhile would have put another in its place, and dropped it.
                const map = await ProjectMapController.getMap(projectId, mapId);
                this.setState(state => ({loadedMaps: {...state.loadedMaps, [mapId]: map}}));
            }
        } catch (e) {
            // The window is closed whatever went wrong. Most failures say so with a toast of their own; a map the release lacks closes quietly (R45).
            // A kept window that the read limit refused is kept still, with its stored place, if it is still open: the user did not close it and its map did not
            // answer 404. A window the user closed meanwhile (a burst read waits up to a minute) stays dropped.
            if (isReadLimited(e) && kept && this.state.windows.some(window => window.mapId === mapId)) {
                this.keptWindows = keptBefore.filter(window => window.mapId === mapId || this.keptWindows.some(now => now.mapId === window.mapId));
            }
            this.closeWindow(mapId);
            if (isMapNotFound(e)) return;
            if (e instanceof UnreadableMapError) {
                this.alert(COPY.mapUnreadable, "danger");
            } else if (isReadLimited(e)) {
                // The limiter has told the user of a burst with its toast (10 s), which the window outlasted by up to a minute, and showed the day's modal when the
                // cap first came. A click that is refused says why nothing opened, so that it is never silent: the day's text for the cap, COPY.generic for a burst
                // (copyFor would promise a wait, and the window is closed). Not while the same text is on screen: an alert of the same text replaces a toast,
                // and the modal must not be replaced.
                const text = isDailyCap(e) ? COPY.map_read_daily_cap : COPY.generic;
                if (!this.state.toasts.some(toast => toast.message === text)) {
                    this.alert(text, "danger");
                }
            } else if (e instanceof ApiError && e.status === 409 && e.is("project_locked")) {
                // The project is converting under another request. The gate's other 409s are not that, and are reported like any other failure.
                this.alert(COPY.project_locked, "danger");
            } else {
                console.error(e);
                reportError(e, {projectId, mapName: mapId});
                this.alert(COPY.generic, "danger");
            }
        }
    }

    closeWindow(mapId) {
        const windows = this.state.windows.filter(window => window.mapId !== mapId);

        this._storeWindows(windows);
        this.setState({windows});
    }

    getSelectedWindow() {
        return Windows.getFrontWindow(this.state.windows);
    }

    setSelectedWindow(mapId) {
        const windows = Windows.bringWindowToFront(mapId, this.state.windows);

        this._storeWindows(windows);
        this.setState({windows});
    }

    async _checkNetworkConnection() {
        const apiConnected = await ping();

        if (apiConnected && !this.state.networkConnected){
            this.alert("Network connection restored.", "Success");
            this.setState({networkConnected: true, blackout: false})
            // The API can be reached again, so the edits whose save got no answer, and whose map could not be read either, are sent again. Their maps are
            // marked Not saved and say that their state is unknown (the Fable ruling, R7).
            mapActions.resendUnknownMaps(this.ctx());
        }

        if (!apiConnected) this.setState({networkConnected: false, blackout: true});
    }

    confirmDisclaimer() {
        UIController.confirmDisclaimer(projectId);
        const disclaimerConfirmed = UIController.isDisclaimerConfirmed(projectId);
        this.setState({disclaimerConfirmed})
    }

    toggleFullScreen() {
        this.setState({fullScreen: !this.state.fullScreen});
    }

    setFullScreen(fullScreen) {
        this.setState({fullScreen});
    }

    /**
     * Creates a new alert toast
     * @param {string} message - the message to display
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     */
    alert(message, bgColour) {
        this._setToast(message, bgColour, "alert", 10000)
    }

    /**
     * Creates a new confirm toast
     * @param {string} message - the message to display
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     * @param {boolean} [cancelable] - should there be a cancel button
     * @return {Promise} success - promise that resolves on success
     */
    confirm(message, bgColour, cancelable=true) {
        return new Promise((resolve) => {
            this._setToast(message, bgColour, "confirm", 0, cancelable, success => {
                resolve(success)
            });
        });
    }

    /**
     * Sets a new toast. A toast whose text is that of a toast on show replaces it. A pending confirm (a confirm whose callback still awaits an answer) is the exception: an alert never replaces it, because whatever waits on its answer (a map's queue behind a Not saved question) would never go on. A confirm that replaces a confirm joins it: the callbacks of the
     * toasts it replaces go into its own, in the order the questions were asked, so that the one answer settles every question that asked the same
     * thing. (Until then the replaced toast's promise never settled, and whatever waited on it never finished.)
     * @param {string} message - the message to display
     * @param {string} type - 'confirm' or 'alert'. Alert displays message, confirm displays message and ok button
     * @param {string} bgColour - bg colour for toast 'Brand', 'Success', 'Neutral' and 'Danger'
     * @param {number} timeout - any number other than 0 will provide a timeout time for toast (in ms). If no timeout provided, toast will not timeout.
     * @param {boolean} [cancelable] - for type 'confirm' should there be a cancel button
     * @param {function} [callBack] - callback function that describes an action. Params ({bool} success - true if ok, false if cancel)
     */
    _setToast(message, bgColour, type, timeout, cancelable=true, callBack=null) {
        this.setState(prevState => {
            const toasts = prevState.toasts.filter(toast => toast.message !== message || (type !== "confirm" && isPendingConfirm(toast)));
            const joined = type === "confirm" ? prevState.toasts.filter(toast => toast.message === message && isPendingConfirm(toast)) : [];
            const answer = joined.length === 0 ? callBack : success => [...joined.map(toast => toast.callBack), callBack].forEach(settle => settle && settle(success));

            bgColour = bgColour.charAt(0).toUpperCase() + bgColour.slice(1).toLowerCase(); //sanitise bgColour

            const id = shortid.generate();

            if (timeout !== 0) window.setTimeout(() => this.unsetToast(id), timeout);

            return {toasts: [...toasts, {id, message, type, bgColour, cancelable, callBack: answer}]}
        });
    }

    /**
     * Unsets the current toast
     * @param id - index of the new toast
     */
    unsetToast(id) {
        this.setState(prevState => {
            const toasts = prevState.toasts.filter(toast => toast.id !== id);
            return {...prevState, toasts}
        });
    }

    async reportMapIssue(mapId, message) {
        const map = this.state.loadedMaps[mapId];
        const projectId = this.state.id;

        if(!map) {
            this.alert("Map not found. Failed to report map, please try again.", "danger");
            return;
        }

        let finalMessage = "";
        let mapIdString = `Reported Map: ${mapId}`
        finalMessage = `${mapIdString}\n\n${message}`

        try {
            await TicketController.createMapReportTicket(map.name, projectId, finalMessage);
            this.alert("Thank you for reporting this map, one of our engineers will be in touch soon.", "success");
        } catch (error) {
            this.alert("Failed to report map, please try again.", "danger");
        }
    };

    render() {
        return (
            <Context.Provider value={this.state}>
                {this.props.children}
            </Context.Provider>
        );
    }
}